import React, { useRef, useCallback, memo } from 'react';
import { Button, Progress, Card, CardBody } from '@heroui/react';
import {
  UploadSimple,
  File,
  Trash,
  Warning,
  Image as ImageIcon,
  FilePdf,
  FileDoc,
  FileXls,
  VideoCamera
} from '@phosphor-icons/react';
import { useMassMailContext } from '../context/MassMailContext';
import { AttachedFile } from '../types';
import { isFileTypeAllowed, formatFileSize as sharedFormatFileSize, FILE_INPUT_ACCEPT, MAX_FILE_SIZE } from 'shared/utils/fileValidation';

// File type icons
const getFileIcon = (type: string) => {
  if (type.startsWith('image/')) return <ImageIcon size={16} className="text-blue-500" />;
  if (type.startsWith('video/')) return <VideoCamera size={16} className="text-purple-500" />;
  if (type.includes('pdf')) return <FilePdf size={16} className="text-red-500" />;
  if (type.includes('doc')) return <FileDoc size={16} className="text-blue-600" />;
  if (type.includes('sheet') || type.includes('excel')) return <FileXls size={16} className="text-green-600" />;
  return <File size={16} className="text-gray-500" />;
};

// Use shared formatFileSize
const formatFileSize = sharedFormatFileSize;

export const AttachmentManager: React.FC = memo(() => {
  const { state, dispatch } = useMassMailContext();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback((files: FileList | File[]) => {
    const newFiles: AttachedFile[] = [];

    Array.from(files).forEach((file) => {
      // Check file type using shared validation
      if (!isFileTypeAllowed(file)) {
        alert(`Bestandstype niet toegestaan: "${file.name}"`);
        return;
      }

      // Check file size (25MB per file)
      if (file.size > MAX_FILE_SIZE) {
        alert(`Bestand "${file.name}" is te groot (max 25MB)`);
        return;
      }

      // Check if file already exists
      const exists = state.attachedFiles.some(f =>
        f.name === file.name && f.size === file.size
      );

      if (!exists) {
        const attachedFile: AttachedFile = {
          id: `file_${Date.now()}_${Math.random()}`,
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          uploadProgress: 0,
          isUploading: false,
          previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined
        };
        
        newFiles.push(attachedFile);
      }
    });
    
    if (newFiles.length > 0) {
      dispatch({
        type: 'SET_ATTACHED_FILES',
        payload: [...state.attachedFiles, ...newFiles]
      });
    }
  }, [state.attachedFiles, dispatch]);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };

  const removeFile = (fileId: string) => {
    const fileToRemove = state.attachedFiles.find(f => f.id === fileId);
    if (fileToRemove && fileToRemove.previewUrl) {
      URL.revokeObjectURL(fileToRemove.previewUrl);
    }
    
    const updatedFiles = state.attachedFiles.filter(f => f.id !== fileId);
    dispatch({ type: 'SET_ATTACHED_FILES', payload: updatedFiles });
  };

  const getTotalSize = () => {
    return state.attachedFiles.reduce((total, file) => total + file.size, 0);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Bijlagen</h3>
        <div className="flex items-center gap-2">
          {state.attachedFiles.length > 0 && (
            <span className="text-sm text-gray-500">
              {state.attachedFiles.length} bestand{state.attachedFiles.length !== 1 ? 'en' : ''} 
              ({formatFileSize(getTotalSize())})
            </span>
          )}
          <Button
            size="sm"
            color="primary"
            variant="flat"
            startContent={<UploadSimple size={14} />}
            onPress={() => fileInputRef.current?.click()}
          >
            Selecteer bestanden
          </Button>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileInputChange}
        className="hidden"
        accept={FILE_INPUT_ACCEPT}
      />

      {/* Drag & Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className="border-2 border-dashed border-gray-300 hover:border-gray-400 transition-colors rounded-lg p-6 text-center cursor-pointer"
        onClick={() => fileInputRef.current?.click()}
      >
        <UploadSimple size={32} className="mx-auto mb-2 text-gray-400" />
        <p className="text-sm text-gray-600">
          Sleep bestanden hierheen of <span className="text-blue-500 font-medium">klik om te selecteren</span>
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Ondersteund: PDF, Word, Excel, afbeeldingen, video's (max 25MB per bestand)
        </p>
      </div>

      {/* Attached Files List */}
      {state.attachedFiles.length > 0 && (
        <div className="space-y-2">
          {state.attachedFiles.map((file) => (
            <Card key={file.id} className="w-full">
              <CardBody className="p-3">
                <div className="flex items-center gap-3">
                  <div className="shrink-0">
                    {getFileIcon(file.type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.size)} • {file.type}
                    </p>
                    
                    {file.isUploading && (
                      <Progress
                        value={file.uploadProgress}
                        className="mt-2"
                        size="sm"
                        color="primary"
                      />
                    )}
                    
                    {file.uploadError && (
                      <div className="flex items-center gap-1 mt-1">
                        <Warning size={12} className="text-red-500" />
                        <span className="text-xs text-red-600">{file.uploadError}</span>
                      </div>
                    )}
                  </div>
                  
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    color="danger"
                    onPress={() => removeFile(file.id)}
                  >
                    <Trash size={14} />
                  </Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {/* File size warning */}
      {getTotalSize() > 25 * 1024 * 1024 && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <Warning size={16} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Waarschuwing:</p>
              <p>
                Totale bestandsgrootte is {formatFileSize(getTotalSize())}. 
                Grote bijlagen kunnen de verzending vertragen of blokkeren.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
